import { FabricImage, StaticCanvas } from 'fabric';

/**
 * Client-side slide → image export from the fabric object model.
 *
 * Replaces the legacy server-side round-trip (raw SVG → server re-render → image URL): the
 * drawing objects, authored in a fixed logical coordinate space, are rebuilt onto an OFFSCREEN
 * canvas together with the chosen background variant, rendered to a canvas element and encoded
 * via the native async `canvas.toBlob` — binary end-to-end, no base64 (`canvas-slide-favorites`
 * D1). The caller owns the resolution policy via `multiplier` (D3); variant derivation from the
 * master lives in `derive-image-variants.ts`.
 */

/** Encoding shared by the master export AND all derived variants (`canvas-slide-favorites` D3). */
export const DEFAULT_EXPORT_MIME_TYPE = 'image/webp';
/** Quality shared by the master export AND all derived variants — change here changes both. */
export const DEFAULT_EXPORT_QUALITY = 0.9;

export interface SlideExportOptions {
    /** Logical canvas width the objects were authored in (e.g. 1280). */
    width: number;
    /** Logical canvas height the objects were authored in (e.g. 720). */
    height: number;
    /** Background image URL composited under the objects. `null`/omitted → blank background. */
    backgroundUrl?: string | null;
    /** Solid fill used when no background image is given. Default `#ffffff`. */
    backgroundColor?: string;
    /** Output scale relative to the logical size. Default `1`. */
    multiplier?: number;
    /**
     * Requested encoding. Default `image/webp`. Browsers without an encoder for the type fall
     * back (typically PNG) — read the returned Blob's `type` for what was actually produced.
     */
    mimeType?: string;
    /** Quality 0..1 for lossy encodings. Default `0.9`. */
    quality?: number;
}

/** Minimal encodable-canvas surface returned by `toCanvasElement` (real `HTMLCanvasElement` satisfies it). */
export interface ExportEncodableCanvas {
    toBlob(callback: (blob: Blob | null) => void, type?: string, quality?: number): void;
}

/** Minimal offscreen-canvas surface used by the exporter (real `StaticCanvas` satisfies it; mockable in tests). */
export interface OffscreenExportCanvas {
    backgroundImage?: unknown;
    loadFromJSON(json: object): Promise<unknown>;
    renderAll(): void;
    toCanvasElement(multiplier?: number): ExportEncodableCanvas;
    dispose(): Promise<unknown>;
}

/** Minimal background-image surface used by the exporter (real `FabricImage` satisfies it). */
export interface ExportBackgroundImage {
    width?: number;
    height?: number;
    set(options: Record<string, unknown>): void;
}

/** Injectable side-effecting dependencies, so {@link exportObjectsToBlob} is unit-testable without a real canvas/DOM. */
export interface ExportDeps {
    createCanvas?: (options: { width: number; height: number; backgroundColor: string }) => OffscreenExportCanvas;
    loadImage?: (url: string) => Promise<ExportBackgroundImage>;
}

/** A serialized fabric object (the shape produced by `canvas.toObject().objects[n]`). */
export type SerializedFabricObject = Record<string, unknown>;

/**
 * Drop objects flagged as non-transferable / non-content so they never reach the export.
 *
 * Honors fabric's own `excludeFromExport` flag (which already omits objects from
 * `toObject()`, so this is mostly belt-and-suspenders) plus a reserved `_noExport`
 * convention for overlay objects — currently unset anywhere (fabric hotspots are
 * geometry-only and contribute no objects); kept as the escape hatch for future overlays.
 */
export function filterExportableObjects<T extends SerializedFabricObject>(objects: readonly T[]): T[] {
    return objects.filter((o) => o['excludeFromExport'] !== true && o['_noExport'] !== true);
}

/** Snapshot a live canvas's exportable drawing objects (excludes the background and overlays). */
export function snapshotDrawingObjects(canvas: { toObject(): { objects?: unknown[] } }): SerializedFabricObject[] {
    const objects = (canvas.toObject().objects ?? []) as SerializedFabricObject[];
    return filterExportableObjects(objects);
}

function defaultCreateCanvas(options: { width: number; height: number; backgroundColor: string }): OffscreenExportCanvas {
    const el = document.createElement('canvas');
    return new StaticCanvas(el, {
        width: options.width,
        height: options.height,
        backgroundColor: options.backgroundColor,
    }) as unknown as OffscreenExportCanvas;
}

async function defaultLoadImage(url: string): Promise<ExportBackgroundImage> {
    return FabricImage.fromURL(url, { crossOrigin: 'anonymous' });
}

/**
 * Render serialized drawing objects onto an offscreen canvas with the given background and
 * encode the result via native `toBlob` (async encode, binary result — never a base64 data
 * URL). Creates and disposes its own canvas; pure with respect to the caller's live canvas.
 *
 * Rejects when the encoder yields no data (null blob); a TAINTED canvas instead makes
 * `toBlob` throw synchronously inside the promise executor — both paths reject, and
 * callers abort the save rather than uploading a broken favorite.
 */
export async function exportObjectsToBlob(
    objects: readonly SerializedFabricObject[],
    options: SlideExportOptions,
    deps: ExportDeps = {},
): Promise<Blob> {
    const {
        width,
        height,
        backgroundUrl = null,
        backgroundColor = '#ffffff',
        multiplier = 1,
        mimeType = DEFAULT_EXPORT_MIME_TYPE,
        quality = DEFAULT_EXPORT_QUALITY,
    } = options;
    const createCanvas = deps.createCanvas ?? defaultCreateCanvas;
    const loadImage = deps.loadImage ?? defaultLoadImage;

    const canvas = createCanvas({ width, height, backgroundColor });
    try {
        await canvas.loadFromJSON({ objects: filterExportableObjects(objects) });

        if (backgroundUrl) {
            const img = await loadImage(backgroundUrl);
            const nativeWidth = img.width ?? width;
            const nativeHeight = img.height ?? height;
            // Scale the variant to cover the logical canvas; `multiplier` scales the output.
            img.set({
                left: 0,
                top: 0,
                originX: 'left',
                originY: 'top',
                scaleX: width / nativeWidth,
                scaleY: height / nativeHeight,
                selectable: false,
                evented: false,
            });
            canvas.backgroundImage = img;
        }

        canvas.renderAll();
        const rendered = canvas.toCanvasElement(multiplier);
        return await new Promise<Blob>((resolve, reject) => {
            rendered.toBlob(
                (blob) => (blob ? resolve(blob) : reject(new Error('Canvas image encoding returned no data'))),
                mimeType,
                quality,
            );
        });
    } finally {
        await canvas.dispose();
    }
}
